import UserService from '@/services/UserService';
import { DeviceUtils } from '@/pixel/index';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';
import Tracking from '@/components/commons/Tracking.js';
import Fingerprint2 from 'fingerprintjs2';
import hash from 'object-hash';
import Chronicler from '@/components/commons/Chronicler.js';
import { gql } from '@apollo/client/core';
import { executeQuery } from '@/components/commons/utils.js';
import { reactive, toRefs } from 'vue';

const EXP_QUERY = gql`
  query ALCHEMIST_SESSION($hashKey: String, $identityId: String, $deviceId: String, $isMobile: Boolean) {
    getAlchemistSession(hashKey: $hashKey, identityId: $identityId, deviceId: $deviceId, isMobile: $isMobile) {
      message
      success

      session {
        hashKey
        features
      }
    }
  }
`;

const state = reactive({
  features: {},
  sessionId: null,
  loaded: false,
});

async function startSession(hashKey, identityId, deviceId, isMobile) {
  try {
    const result = await executeQuery(
      EXP_QUERY,
      {
        hashKey,
        identityId,
        deviceId,
        isMobile,
      },
      { fetchPolicy: 'network-only' },
    );

    if (!result || !result.getAlchemistSession || !result.getAlchemistSession.session) {
      Tracking.sentryCaptureError(new Error('Failed to load Alchemist session data'));
      return null;
    }

    return result.getAlchemistSession.session;
  } catch (error) {
    Tracking.sentryCaptureError(error);
  }
  return null;
}

export async function init() {
  if (import.meta.env.SSR) {
    return;
  }

  storePermanentFeatureOverrides();

  state.loaded = false;

  let fingerPrint = LocalStorage.get(LocalStorageKeys.DEVICE_FINGERPRINT);
  const session = JSON.parse(LocalStorage.get(LocalStorageKeys.ALCHEMIST_SESSION) || null);

  if (!fingerPrint) {
    fingerPrint = await generateFingerprint();
  }

  if (session && session.id) {
    const isFresh = new Date() - new Date(session.lastUpdated) < 60 * 60 * 1000;
    if (isFresh) {
      initFromCache(session);
      return;
    }
  }
  await initFromServer(session, fingerPrint, DeviceUtils.isMobile());
}

async function generateFingerprint() {
  const components = await Fingerprint2.getPromise();
  const newFingerPrint = hash(components);
  LocalStorage.set(LocalStorageKeys.DEVICE_FINGERPRINT, newFingerPrint);
  return newFingerPrint;
}

function initFromCache(session) {
  state.sessionId = session.id;
  state.features = Object.assign({}, session.features, getFeatureOverrides());

  LocalStorage.set(
    LocalStorageKeys.ALCHEMIST_SESSION,
    JSON.stringify({
      id: session.id,
      features: session.features,
      lastUpdated: session.lastUpdated,
    }),
  );

  state.loaded = true;
  attachChroniclerSession({ track: false });
}

async function initFromServer(session, fingerPrint, isMobile) {
  try {
    const sessionId = session ? session.id : state.sessionId;
    const updatedSession = await startSession(sessionId, UserService.getSessionId(), fingerPrint, isMobile);

    state.sessionId = updatedSession ? updatedSession.hashKey : state.sessionId;
    state.features = Object.assign({}, updatedSession ? updatedSession.features || {} : {}, getFeatureOverrides());

    LocalStorage.set(
      LocalStorageKeys.ALCHEMIST_SESSION,
      JSON.stringify({
        id: state.sessionId,
        features: state.features,
        lastUpdated: new Date(),
      }),
    );

    state.loaded = true;
    attachChroniclerSession({ track: true });
  } catch (e) {
    Tracking.sentryCaptureError(e);
  }
}

function getFeatureOverrides() {
  const features = getPermanentFeatureOverrides();
  if (import.meta.env.SSR) {
    return features;
  }
  const urlParams = new URLSearchParams(window.location.search);

  // Deprecated override feature pattern
  let overrides = urlParams.get('override-feature');
  if (overrides) {
    const [key, value] = overrides.split(',');
    if (key && value) {
      features[key] = value;
    }
  }

  // Latest override feature pattern
  overrides = urlParams.getAll('exp');
  overrides.forEach(exp => {
    const [key, value] = exp.split(':');
    if (key && value) {
      features[key] = value;
    }
  });

  return features;
}

function storePermanentFeatureOverrides() {
  if (import.meta.env.SSR) {
    return;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const clearOverrides = urlParams.has('exp-clear');
  const features = clearOverrides ? {} : getPermanentFeatureOverrides();
  const overrides = urlParams.getAll('exp-perm');
  let updated = clearOverrides;
  overrides.forEach(exp => {
    const [key, value] = exp.split(':');
    if (key && value) {
      features[key] = value;
      updated = true;
    }
  });
  if(updated) {
    try {
      LocalStorage.set(LocalStorageKeys.ALCHEMIST_OVERRIDES, JSON.stringify(features));
    } catch(e) {}
  }
}

function getPermanentFeatureOverrides() {
  if (import.meta.env.SSR) {
    return {};
  }

  try {
    const features = JSON.parse(LocalStorage.get(LocalStorageKeys.ALCHEMIST_OVERRIDES) || '{}');
    return features || {};
  } catch(e) {}
  return {};
}

function attachChroniclerSession(options) {
  Chronicler.attach({
    features: state.features,
    sessionId: state.sessionId,
  });
  if(options && options.track) {
    Chronicler.track('Experiments Loaded');
  }
  Chronicler.throttledTrackQueuedEvents();
}

export default function useExperiment() {
  function getFeatureVariant(featureName) {
    return state.features[featureName];
  }

  function isFeatureVariant(featureName, featureVariant) {
    return state.features[featureName] === featureVariant;
  }

  return {
    ...toRefs(state),
    getFeatureVariant,
    isFeatureVariant,
  };
}
